import { gql } from '@apollo/client';
import type { NewVehicleGroup, VehicleGroup, VehicleGroupWithPath } from '@cobliteam/common-types';
import type { MutationCreateVehiclesGroupArgs, MutationUpdateVehiclesGroupArgs, VehiclesGroup, VehiclesGroupInput } from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import { graphQLVehiclesGroupToVehicleGroup, graphQLVehiclesGroupToVehicleGroupWithPath } from './VehicleGroupTransformer';

export class GraphQLVehicleGroupsRequester {
  public getVehicleGroups = (groupIds?: string[]): Promise<VehicleGroup[]> => {
    return graphQLRequester
      .query<{ vehicleGroups: VehiclesGroup[] }>({
        query: gql`
          query vehicleGroups($groupIds: [String]) {
            vehicleGroups(groupIds: $groupIds) {
              id
              name
              createdAt
              updatedAt
              creatorId
              path
              vehicles {
                id
              }
            }
          }
        `,
        variables: {
          groupIds: groupIds,
        },
      })
      .then((graphQlResponse) => {
        return graphQlResponse.vehicleGroups?.map(graphQLVehiclesGroupToVehicleGroup) || [];
      });
  };

  public getVehicleGroupsWithPaths = (): Promise<VehicleGroupWithPath[]> => {
    return graphQLRequester
      .query<{ vehicleGroups: VehiclesGroup[] }>({
        query: gql`
          query vehicleGroupsWithPath {
            vehicleGroups {
              id
              name
              path
            }
          }
        `,
      })
      .then((graphQlResponse) => graphQlResponse.vehicleGroups.map(graphQLVehiclesGroupToVehicleGroupWithPath));
  };

  public getVehicleGroupsWithUsers = (groupIds?: string[]): Promise<VehiclesGroup[]> => {
    return graphQLRequester
      .query<{ vehicleGroups: VehiclesGroup[] }>({
        query: gql`
          query vehicleGroupsWithUsers($groupIds: [String]) {
            vehicleGroups(groupIds: $groupIds) {
              id
              name
              createdAt
              updatedAt
              creatorId
              vehicles {
                id
              }
              users {
                id
                groups_restriction
              }
            }
          }
        `,
        variables: {
          groupIds: groupIds,
        },
      })
      .then((response) => response.vehicleGroups);
  };

  public createVehicleGroup = (fleetId: string, newVehicleGroup: NewVehicleGroup): Promise<void> => {
    const vehicleGroupInput: VehiclesGroupInput = {
      name: newVehicleGroup.name,
      vehicleIds: newVehicleGroup.vehicleIds || [],
      userIds: newVehicleGroup.userIds || [],
      fleetId: fleetId,
    };

    return graphQLRequester
      .mutate<{ createVehiclesGroup: VehiclesGroup }, MutationCreateVehiclesGroupArgs>({
        mutation: gql`
          mutation CreateVehiclesGroup($input: VehiclesGroupInput!) {
            createVehiclesGroup(input: $input) {
              id
              name
              fleetId
              createdAt
              updatedAt
              creatorId
              vehicles {
                id
              }
            }
          }
        `,
        variables: {
          input: vehicleGroupInput,
        },
      })
      .then();
  };

  public updateVehicleGroup = (fleetId: string, vehicleGroup: VehicleGroup): Promise<void> => {
    const vehicleGroupInput: VehiclesGroupInput = {
      name: vehicleGroup.name,
      vehicleIds: vehicleGroup.vehicleIds || [],
      userIds: vehicleGroup.userIds || [],
      fleetId: fleetId,
    };

    return graphQLRequester
      .mutate<{ updateVehiclesGroup: VehiclesGroup }, MutationUpdateVehiclesGroupArgs>({
        mutation: gql`
          mutation UpdateVehiclesGroup($id: ID!, $input: VehiclesGroupInput!) {
            updateVehiclesGroup(id: $id, input: $input) {
              id
              name
              fleetId
              createdAt
              updatedAt
              creatorId
              vehicles {
                id
              }
            }
          }
        `,
        variables: {
          id: vehicleGroup.id,
          input: vehicleGroupInput,
        },
      })
      .then();
  };
}
